import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('userRole', user.role);
      setUser(user);

      toast.success('تم تسجيل الدخول بنجاح');

      if (user.role === 'legal_manager') {
        navigate('/companies');
      } else {
        navigate('/executive');
      }
    } catch (error) {
      toast.error('فشل تسجيل الدخول. يرجى التحقق من البيانات.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* Right side - Image */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-12">
          <div className="max-w-md text-center space-y-6">
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_contract-portal-1/artifacts/qhuwq0xk_IFG%20logo.png"
                alt="IFG Holding Logo"
                className="h-40 mx-auto"
              />
            </div>
            <h1 className="text-5xl font-bold tracking-tight">
              نظام إدارة الشؤون القانونية
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed">
              منصة لإدارة العقود والقضايا القانونية لمجموعة IFG
            </p>
          </div>
        </div>
      </div>

      {/* Left side - Login Form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_contract-portal-1/artifacts/qhuwq0xk_IFG%20logo.png"
              alt="IFG Holding Logo"
              className="h-24 mx-auto mb-6"
            />
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              تسجيل الدخول
            </h2>
            <p className="text-slate-600">
              الرجاء إدخال بيانات الدخول للوصول إلى النظام
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-right block text-sm font-medium text-slate-700">
                البريد الإلكتروني
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@ifg.com"
                required
                className="w-full text-right h-12 bg-white border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-right block text-sm font-medium text-slate-700">
                كلمة المرور
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full text-right h-12 bg-white border-slate-300 pl-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="text-left">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#E94B7B] hover:text-[#d43a6a] transition-colors"
                  data-testid="forgot-password-link"
                >
                  نسيت كلمة المرور؟
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#E94B7B] hover:bg-[#d43a6a] text-white font-medium text-base shadow-md"
            >
              {loading ? 'جارٍ تسجيل الدخول...' : 'تسجيل الدخول'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}